"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.sha256Hash = exports.signSecp256r1Message = exports.signSecp256r1SessionTx = exports.calcSessionSignedWitnessLock = exports.signSecp256r1Tx = exports.calcSignedWitnessLock = void 0;
const ckb_sdk_utils_1 = require("@nervosnetwork/ckb-sdk-utils");
const fast_sha256_1 = require("fast-sha256");
const blake2b_1 = require("@nervosnetwork/ckb-sdk-utils/lib/crypto/blake2b");
const utils_1 = require("../utils");
const constants_1 = require("../constants");
const calcSignedWitnessLock = (key, transaction, mode = constants_1.WITNESS_NATIVE_MODE) => {
    if (!key)
        throw new Error('Private key or address object');
    const witnessGroup = transaction.witnesses;
    if (!witnessGroup.length) {
        throw new Error('WitnessGroup cannot be empty');
    }
    if (typeof witnessGroup[0] !== 'object') {
        throw new Error('The first witness in the group should be type of WitnessArgs');
    }
    const transactionHash = (0, ckb_sdk_utils_1.rawTransactionToHash)(transaction);
    const emptyWitness = Object.assign(Object.assign({}, witnessGroup[0]), { lock: `0x${'0'.repeat(constants_1.SECP256R1_PUBKEY_SIG_LEN)}` });
    const serializedEmptyWitnessBytes = (0, ckb_sdk_utils_1.hexToBytes)((0, ckb_sdk_utils_1.serializeWitnessArgs)(emptyWitness));
    const serializedEmptyWitnessSize = serializedEmptyWitnessBytes.length;
    const hasher = (0, blake2b_1.default)(32, null, null, ckb_sdk_utils_1.PERSONAL);
    hasher.update((0, ckb_sdk_utils_1.hexToBytes)(transactionHash));
    hasher.update((0, ckb_sdk_utils_1.hexToBytes)((0, ckb_sdk_utils_1.toUint64Le)(`0x${serializedEmptyWitnessSize.toString(16)}`)));
    hasher.update(serializedEmptyWitnessBytes);
    witnessGroup.slice(1).forEach(w => {
        const bytes = (0, ckb_sdk_utils_1.hexToBytes)(typeof w === 'string' ? w : (0, ckb_sdk_utils_1.serializeWitnessArgs)(w));
        hasher.update((0, ckb_sdk_utils_1.hexToBytes)((0, ckb_sdk_utils_1.toUint64Le)(`0x${bytes.length.toString(16)}`)));
        hasher.update(bytes);
    });
    const message = `${hasher.digest('hex')}`;
    const base64 = Buffer.from(message).toString('base64url');
    const sighashAll = Buffer.from(base64, 'utf8').toString('hex');
    const pubKey = (0, utils_1.getPublicKey)(key);
    const authData = '49960de5880e8c687434170f6476605b8fe4aeb9a28632c7995cf3ba831d97630162f9fb77';
    const clientData = `7b2274797065223a22776562617574686e2e676574222c226368616c6c656e6765223a22${sighashAll}222c226f726967696e223a22687474703a2f2f6c6f63616c686f73743a38303030222c2263726f73734f726967696e223a66616c73657d`;
    const clientDataHash = (0, exports.sha256Hash)(clientData);
    const signData = `0x${authData}${clientDataHash}`;
    const signature = (0, exports.signSecp256r1Message)(key, signData);
    return `${mode}${pubKey}${signature}${authData}${clientData}`;
};
exports.calcSignedWitnessLock = calcSignedWitnessLock;
const signSecp256r1Tx = (key, transaction, mode = constants_1.WITNESS_NATIVE_MODE) => {
    const witnessGroup = transaction.witnesses;
    if (!witnessGroup.length) {
        throw new Error('WitnessGroup cannot be empty');
    }
    if (typeof witnessGroup[0] !== 'object') {
        throw new Error('The first witness in the group should be type of WitnessArgs');
    }
    const emptyWitness = Object.assign(Object.assign({}, witnessGroup[0]), { lock: `0x${(0, exports.calcSignedWitnessLock)(key, transaction, mode)}` });
    const signedWitnesses = [(0, ckb_sdk_utils_1.serializeWitnessArgs)(emptyWitness), ...witnessGroup.slice(1)];
    return Object.assign(Object.assign({}, transaction), { witnesses: signedWitnesses.map(witness => (typeof witness === 'string' ? witness : (0, ckb_sdk_utils_1.serializeWitnessArgs)(witness))) });
};
exports.signSecp256r1Tx = signSecp256r1Tx;
const calcSessionSignedWitnessLock = (key, sessionKey, transaction, mode = constants_1.WITNESS_NATIVE_SESSION_MODE) => {
    if (!key)
        throw new Error('Private key or address object');
    const witnessGroup = transaction.witnesses;
    if (!witnessGroup.length) {
        throw new Error('WitnessGroup cannot be empty');
    }
    if (typeof witnessGroup[0] !== 'object') {
        throw new Error('The first witness in the group should be type of WitnessArgs');
    }
    const transactionHash = (0, ckb_sdk_utils_1.rawTransactionToHash)(transaction);
    const emptyWitness = Object.assign(Object.assign({}, witnessGroup[0]), { lock: '0x' });
    const serializedEmptyWitnessBytes = (0, ckb_sdk_utils_1.hexToBytes)((0, ckb_sdk_utils_1.serializeWitnessArgs)(emptyWitness));
    const serializedEmptyWitnessSize = serializedEmptyWitnessBytes.length;
    const hasher = (0, blake2b_1.default)(32, null, null, ckb_sdk_utils_1.PERSONAL);
    hasher.update((0, ckb_sdk_utils_1.hexToBytes)(transactionHash));
    hasher.update((0, ckb_sdk_utils_1.hexToBytes)((0, ckb_sdk_utils_1.toUint64Le)(`0x${serializedEmptyWitnessSize.toString(16)}`)));
    hasher.update(serializedEmptyWitnessBytes);
    witnessGroup.slice(1).forEach(w => {
        const bytes = (0, ckb_sdk_utils_1.hexToBytes)(typeof w === 'string' ? w : (0, ckb_sdk_utils_1.serializeWitnessArgs)(w));
        hasher.update((0, ckb_sdk_utils_1.hexToBytes)((0, ckb_sdk_utils_1.toUint64Le)(`0x${bytes.length.toString(16)}`)));
        hasher.update(bytes);
    });
    const message = `0x${hasher.digest('hex')}`;
    if (sessionKey.getKeySize() !== 2048) {
        throw new Error('RSA key size error');
    }
    const signature = (0, utils_1.signRSAMessage)(sessionKey, message);
    // Build sessoin message and attestation with secp256r1
    const sessionVer = '00';
    const sessionPubkey = (0, utils_1.exportPubKey)(sessionKey);
    const sessionMessage = (0, utils_1.blake256)(`0x${sessionVer}${sessionPubkey}`);
    const base64 = Buffer.from(sessionMessage).toString('base64url');
    const attestationMessage = Buffer.from(base64, 'utf8').toString('hex');
    const pubKey = (0, utils_1.getPublicKey)(key);
    const authData = '49960de5880e8c687434170f6476605b8fe4aeb9a28632c7995cf3ba831d97630162f9fb77';
    const clientData = `7b2274797065223a22776562617574686e2e676574222c226368616c6c656e6765223a22${attestationMessage}222c226f726967696e223a22687474703a2f2f6c6f63616c686f73743a38303030222c2263726f73734f726967696e223a66616c73657d`;
    const clientDataHash = (0, exports.sha256Hash)(clientData);
    const signData = `0x${authData}${clientDataHash}`;
    const attestation = (0, exports.signSecp256r1Message)(key, signData);
    return `0x${mode}${sessionPubkey}${signature}${pubKey}${sessionVer}${attestation}${authData}${clientData}`;
};
exports.calcSessionSignedWitnessLock = calcSessionSignedWitnessLock;
const signSecp256r1SessionTx = (key, sessionKey, transaction, mode = constants_1.WITNESS_NATIVE_SESSION_MODE) => {
    const witnessGroup = transaction.witnesses;
    if (!witnessGroup.length) {
        throw new Error('WitnessGroup cannot be empty');
    }
    if (typeof witnessGroup[0] !== 'object') {
        throw new Error('The first witness in the group should be type of WitnessArgs');
    }
    const emptyWitness = Object.assign(Object.assign({}, witnessGroup[0]), { lock: (0, exports.calcSessionSignedWitnessLock)(key, sessionKey, transaction, mode) });
    const signedWitnesses = [(0, ckb_sdk_utils_1.serializeWitnessArgs)(emptyWitness), ...witnessGroup.slice(1)];
    return Object.assign(Object.assign({}, transaction), { witnesses: signedWitnesses.map(witness => (typeof witness === 'string' ? witness : (0, ckb_sdk_utils_1.serializeWitnessArgs)(witness))) });
};
exports.signSecp256r1SessionTx = signSecp256r1SessionTx;
const signSecp256r1Message = (key, message) => {
    if (!message.startsWith('0x')) {
        throw new Error('Message format error');
    }
    const msg = (0, fast_sha256_1.default)((0, ckb_sdk_utils_1.hexToBytes)(message));
    const sig = key.sign(msg);
    let result = key.verify(msg, sig);
    const fmtR = sig.r.toString(16).padStart(64, '0');
    const fmtS = sig.s.toString(16).padStart(64, '0');
    return `${fmtR}${fmtS}`;
};
exports.signSecp256r1Message = signSecp256r1Message;
const sha256Hash = (message) => {
    let hash = (0, fast_sha256_1.default)((0, ckb_sdk_utils_1.hexToBytes)((0, utils_1.append0x)(message)));
    return (0, utils_1.remove0x)((0, ckb_sdk_utils_1.bytesToHex)(hash));
};
exports.sha256Hash = sha256Hash;
